import {Show, HStack, Box, getValueByDevice, useDevice} from 'platform/foundation';
import styled from 'styled-components';

import {useState} from 'react';

import {or} from 'ramda';

import {browserStorageKey} from '@dms/config';
import {MAIN_TOPBAR_HEIGHT} from '@dms/shared';

import {ActivitySidebarProvider} from '../contexts/ActivitySidebarContext';
import {useSidebarHeight} from '../hooks/useSidebarHeight';
import {ActivitySidebarAction} from '../types/ActivitySidebarAction';
import {ActivitySidebarItemV2} from './ActivitySidebarItemV2';
import {ActivitySidebarMenu} from './ActivitySidebarMenu';

interface ActivitySidebarV2Props {
  actions: ActivitySidebarAction[];
}

export function ActivitySidebarV2(props: ActivitySidebarV2Props) {
  const device = useDevice();
  const shouldOpenActivityPanel = localStorage.getItem(browserStorageKey.SALES_ACTIVITY_PANEL_OPEN);
  const {ref: stickyRef, height: sidebarHeight} = useSidebarHeight();

  // On desktop (≥1367px) the panel is in-flow and pushes content left.
  // On notebook/tablet/mobile (<1367px) the panel overlays content using absolute positioning.
  const isOverlay = getValueByDevice(device, true, true, true, false);
  const isMobileOrTablet = or(device === 'mobile', device === 'tablet');

  const [openTab, setOpenTab] = useState(() => {
    if (shouldOpenActivityPanel === 'false') {
      return null;
    }

    if (shouldOpenActivityPanel === 'chat' && !props.actions.some((item) => item.id === 'chat')) {
      return null;
    }

    return shouldOpenActivityPanel;
  });

  const handleCloseTab = () => {
    setOpenTab(null);
    localStorage.setItem(browserStorageKey.SALES_ACTIVITY_PANEL_OPEN, 'false');
  };

  const handleToggleTab = (tabId: string) => {
    const newTab = openTab === tabId ? null : tabId;
    setOpenTab(newTab);
    localStorage.setItem(browserStorageKey.SALES_ACTIVITY_PANEL_OPEN, newTab ?? 'false');
  };

  const activeAction = props.actions.find((action) => action.id === openTab);

  return (
    <ActivitySidebarProvider handleToggleTab={handleToggleTab}>
      {/*
       * StickyContainer uses position: sticky with top: 0.
       *
       * Why top: 0 and not the TopBar height?
       * #dms-app-container starts directly below the TopBar (it is a grid cell in row 2).
       * So top: 0 inside #dms-app-container already means "top of the area below the TopBar".
       * No additional offset is needed.
       *
       * Why align-self: flex-start?
       * Without it, the sticky element stretches to fill its flex parent's full height
       * (align-items: stretch default), making it as tall as the content and removing
       * the sticky effect. align-self: flex-start lets it be shorter than its parent
       * so sticky has something to anchor against.
       *
       * Height is computed by useSidebarHeight which measures the sidebar's actual
       * top position in the viewport and sets height = viewportHeight - sidebarTop.
       * This correctly accounts for the entity header height on initial load and
       * gives the full viewport-minus-TopBar height once the header has scrolled away.
       */}
      <StickyContainer
        ref={stickyRef}
        $isMobileOrTablet={isMobileOrTablet}
        style={{height: isMobileOrTablet ? undefined : sidebarHeight}}
      >
        <HStack spacing={0} height="100%">
          <Show when={openTab}>
            <Box
              position={isOverlay ? 'absolute' : 'initial'}
              zIndex={isMobileOrTablet ? 'HEADER' : isOverlay ? 'CONTENT' : undefined}
              left={isMobileOrTablet ? 0 : undefined}
              right={isMobileOrTablet ? 0 : isOverlay ? 12 : undefined}
              top={isMobileOrTablet ? MAIN_TOPBAR_HEIGHT : undefined}
              bottom={isMobileOrTablet ? 0 : undefined}
              width={isMobileOrTablet ? undefined : 100}
              height={isMobileOrTablet ? undefined : '100%'}
              backgroundColor="general.white"
              borderRight="1px solid"
              borderColor="general.separator"
              boxShadow="elevation_3"
              overflow="hidden"
            >
              <ActivitySidebarItemV2
                title={activeAction?.label ?? ''}
                onClose={handleCloseTab}
                disablePadding={activeAction?.disablePadding}
                actions={props.actions}
                handleToggleTab={handleToggleTab}
                activeTab={openTab}
              >
                {activeAction?.component ?? null}
              </ActivitySidebarItemV2>
            </Box>
          </Show>
          <ActivitySidebarMenu
            actions={props.actions}
            onClick={handleToggleTab}
            activeTab={openTab}
          />
        </HStack>
      </StickyContainer>
    </ActivitySidebarProvider>
  );
}

const StickyContainer = styled.div<{$isMobileOrTablet: boolean}>`
  position: ${({$isMobileOrTablet}) => ($isMobileOrTablet ? 'static' : 'sticky')};
  top: 0;
  align-self: ${({$isMobileOrTablet}) => ($isMobileOrTablet ? 'stretch' : 'flex-start')};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;
